import * as React from 'react';
import { useState } from 'react';
import { Avatar, Box, Button, CircularProgress, InputBase, List, ListItem, ListItemAvatar, ListItemButton, ListItemText, Menu, Typography } from '@mui/material';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import ClearIcon from '@mui/icons-material/Clear';
import SearchIcon from '@mui/icons-material/Search';
import { useTranslation } from 'react-i18next';
import { getFullname, getLetterAvatar } from '../other/ConfigBasic';
import theme from '../../themes/theme';

export default function AssignedDriverSelect({ value, drivers, assignedDriver, disabled, loading, onChange }) {
    const { t } = useTranslation();
    const [anchorEl, setAnchorEl] = useState(null);
    const [searchValue, setSearchValue] = useState('');
    const open = Boolean(anchorEl);

    const options = [...(drivers || [])];
    if (assignedDriver?.id && !options.some((driver) => driver.id === assignedDriver.id)) {
        options.unshift(assignedDriver);
    }

    const selectedDriver = options.find((driver) => driver.id === value) || assignedDriver || null;
    const hasSelection = Boolean(value);
    const driverList = options.filter(
        (driver) =>
            driver?.firstname?.toLowerCase().includes(searchValue.toLowerCase()) ||
            driver?.lastname?.toLowerCase().includes(searchValue.toLowerCase())
    );

    const stopRowEvents = (event) => {
        event.stopPropagation();
    };

    const handleButtonClick = (event) => {
        stopRowEvents(event);
        if (disabled || loading || event.target.closest('.assigned-driver-clear')) {
            return;
        }
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
        setSearchValue('');
    };

    const handleSelect = (driverId) => {
        handleClose();
        onChange(driverId);
    };

    const handleClear = (event) => {
        stopRowEvents(event);
        handleClose();
        onChange(null);
    };

    return (
        <Box onClick={stopRowEvents} onMouseDown={stopRowEvents}>
            <Button
                variant='none'
                color='primary'
                disabled={disabled || loading}
                disableElevation
                onClick={handleButtonClick}
                endIcon={
                    loading ? (
                        <CircularProgress size={12} sx={{ color: '#757575' }} />
                    ) : hasSelection ? (
                        <ClearIcon className='assigned-driver-clear' sx={{ fontSize: '16px' }} onClick={disabled ? undefined : handleClear} />
                    ) : (
                        <ArrowDropDownIcon sx={{ fontSize: '18px' }} />
                    )
                }
                sx={{
                    minWidth: 132,
                    maxWidth: 168,
                    height: 28,
                    minHeight: 28,
                    px: '8px',
                    py: 0,
                    justifyContent: 'space-between',
                    fontSize: '13px',
                    lineHeight: 1.2,
                    fontFamily: 'CircularProMedium',
                    borderRadius: '8px',
                    border: hasSelection ? '' : '1px solid #757575',
                    textTransform: 'none',
                    color: '#212121',
                    overflow: 'hidden',
                    whiteSpace: 'nowrap',
                    backgroundColor: hasSelection ? '#B9F6CA' : '',
                    '& .MuiButton-endIcon': {
                        marginLeft: '4px',
                    },
                    '& .MuiButton-endIcon > *:nth-of-type(1)': {
                        fontSize: '16px',
                    },
                    '&:hover': {
                        backgroundColor: hasSelection ? '#A5E9B8' : '#f5f5f5',
                    },
                    '&.Mui-disabled': {
                        border: hasSelection ? '' : '1px solid #E0E0E0',
                        backgroundColor: hasSelection ? '#E8F5E9' : '',
                        color: '#757575',
                    },
                }}
            >
                <span>{hasSelection ? getFullname(selectedDriver) : t('selectDriver')}</span>
            </Button>
            <Menu
                anchorEl={anchorEl}
                open={open}
                onClose={handleClose}
                MenuListProps={{
                    role: 'listbox',
                    sx: { width: 280 },
                }}
            >
                <Box
                    display='flex'
                    alignItems='center'
                    justifyContent='space-between'
                    padding='0 8px'
                    border={`solid 1px ${theme.palette.grey[600]}`}
                    borderRadius='4px'
                    margin='0 8px 8px 8px'
                >
                    <InputBase placeholder={t('search')} value={searchValue} onChange={(event) => setSearchValue(event.target.value)} />
                    <SearchIcon sx={{ color: theme.palette.grey[600] }} />
                </Box>
                <List dense sx={{ height: '220px', overflow: 'auto' }}>
                    {driverList.length > 0 ? (
                        driverList.map((driver) => (
                            <ListItem key={driver.id} disablePadding>
                                <ListItemButton selected={driver.id === value} onClick={() => handleSelect(driver.id)}>
                                    <ListItemAvatar>
                                        <Avatar
                                            alt={getFullname(driver)}
                                            src={driver.imageUri ? `${process.env.REACT_APP_AWS_CONTACT_PATH}${driver.imageUri}` : ''}
                                            sx={{ color: '#212121', fontSize: '14px', backgroundColor: '#E0E0E0', width: 32, height: 32 }}
                                        >
                                            {getLetterAvatar(driver)}
                                        </Avatar>
                                    </ListItemAvatar>
                                    <ListItemText
                                        primary={getFullname(driver)}
                                        primaryTypographyProps={{
                                            fontSize: '14px',
                                            fontFamily: 'CircularProMedium',
                                            color: '#212121',
                                        }}
                                    />
                                </ListItemButton>
                            </ListItem>
                        ))
                    ) : (
                        <ListItem>
                            <ListItemText>
                                <Typography variant='caption' color='gray'>
                                    {t('search')}
                                </Typography>
                            </ListItemText>
                        </ListItem>
                    )}
                </List>
            </Menu>
        </Box>
    );
}
